import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { PermanentJobsService } from '../services';

/**
 * Factory method to construct the query key for posting details
 *
 * @param {Object} payload - Query key parameters
 * @param {string} payload.postingId - Unique identifier for the posting
 * @returns {Array} Query key array for React Query
 *
 * @example
 * const queryKey = getPostingDetailsQueryKey({ postingId: 'posting-123' });
 * // Returns: ['GET_POSTING_DETAILS', 'posting-123']
 *
 * @example
 * // Invalidate the query after mutation
 * queryClient.invalidateQueries({
 *   queryKey: getPostingDetailsQueryKey({ postingId: 'posting-123' })
 * });
 */
export const getPostingDetailsQueryKey = (payload) => ['GET_POSTING_DETAILS', payload.postingId];

/**
 * React Query hook to fetch posting details
 *
 * @param {Object} params - Hook parameters
 * @param {string} params.postingId - Unique identifier for the posting
 * @param {Object} [options] - Hook options
 * @param {boolean} [options.refetchOnWindowFocus=false] - Whether to refetch when window regains focus
 * @param {boolean} [options.enabled=true] - Whether the query should execute automatically
 */
export const useGetPostingDetails = ({ postingId }, options) => {
  const dispatch = useDispatch();

  const { data, error, isFetching, isLoading, isError } = useQuery({
    queryKey: getPostingDetailsQueryKey({ postingId }),
    queryFn: async () => {
      dispatch({ type: 'GET_POSTING_DETAILS_REQUEST' });

      return PermanentJobsService.getPostingDetails({ postingId }).finally(() =>
        dispatch({ type: 'GET_POSTING_DETAILS_SUCCESS' }),
      );
    },
    retry: false,
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
    enabled: options?.enabled ?? true,
  });

  return {
    data: data || null,
    error,
    isError,
    isInitialLoading: isLoading,
    isLoading: isLoading || isFetching,
  };
};
